export type SnapshotMetric<Key extends string = string> = {
  key: Key;
  current: number;
  previous: number | undefined;
};

export type SnapshotDelta =
  | { kind: 'none' }
  | { kind: 'unchanged' }
  | { kind: 'up'; amount: number }
  | { kind: 'down'; amount: number };

export type SnapshotDeltaCell<Key extends string = string> = {
  key: Key;
  value: number;
  delta: SnapshotDelta;
};

const toDelta = ({
  current,
  previous,
}: {
  current: number;
  previous: number | undefined;
}): SnapshotDelta => {
  if (previous === undefined) {
    return { kind: 'none' };
  }

  if (current > previous) {
    return { kind: 'up', amount: current - previous };
  }

  if (current < previous) {
    return { kind: 'down', amount: previous - current };
  }

  return { kind: 'unchanged' };
};

export const buildSnapshotDeltaCells = <Key extends string>(
  metrics: SnapshotMetric<Key>[],
): SnapshotDeltaCell<Key>[] =>
  metrics.map(({ key, current, previous }) => ({
    key,
    value: current,
    delta: toDelta({ current, previous }),
  }));

export const hasSnapshotBaseline = (metrics: SnapshotMetric[]): boolean =>
  metrics.some((metric) => metric.previous !== undefined);
